import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, Snackbar, Drawer, Divider,
  FormControlLabel, Switch,
} from "@mui/material";
import {
  FaPlus, FaTrash, FaPencilAlt, FaTimes, FaBell,
  FaExclamationTriangle, FaCalendarAlt, FaClock, FaInfoCircle,
} from "react-icons/fa";
import { auth } from "../firebase";

const BASE = "https://vol-backend.onrender.com";

const TIPO_CONFIG = {
  urgente: { label: "Urgente", color: "#e60000", bg: "#fff0f0", icon: <FaExclamationTriangle />, emoji: "🚨" },
  evento: { label: "Evento", color: "#7c3aed", bg: "#f3e8ff", icon: <FaCalendarAlt />, emoji: "📅" },
  recordatorio: { label: "Recordatorio", color: "#1565c0", bg: "#e3f2fd", icon: <FaClock />, emoji: "⏰" },
  informativo: { label: "Informativo", color: "#2e7d32", bg: "#e8f5e9", icon: <FaInfoCircle />, emoji: "📢" },
};

const ESTADO_CONFIG = {
  activo:      { label: "Activo",      bg: "#e8f5e9", color: "#2e7d32" },
  programado:  { label: "Programado",  bg: "#fff8e1", color: "#f57f17" },
  borrador:    { label: "Borrador",    bg: "#f5f5f5", color: "#757575" },
  expirado:    { label: "Expirado",    bg: "#fce4ec", color: "#c62828" },
};

async function apiFetch(path, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("No hay sesión activa");
  const token = await user.getIdToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text() || `Error ${res.status}`);
  return res.json();
}

// ── Vista previa del aviso ───────────────────────────────────
function AvisoPreview({ form }) {
  const t = TIPO_CONFIG[form.tipo] || TIPO_CONFIG.informativo;
  return (
    <Box sx={{ border: `2px solid ${t.color}`, borderRadius: 2, p: 2, backgroundColor: t.bg, mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box sx={{ color: t.color, fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase",
          backgroundColor: t.color, color: "#fff", px: 1, py: 0.2, borderRadius: 1 }}>
          {t.label}
        </Box>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#111", mt: 0.5 }}>
        {form.titulo || "Título del aviso"}
      </Typography>
      <Typography sx={{ fontSize: "0.82rem", color: "#444", mt: 0.5 }}>
        {form.mensaje || "Mensaje del aviso..."}
      </Typography>
      {form.fecha_evento && (
        <Typography sx={{ fontSize: "0.78rem", color: "#666", mt: 0.5 }}>
          📅 {new Date(form.fecha_evento).toLocaleString("es-MX")}
          {form.ubicacion && ` · 📍 ${form.ubicacion}`}
        </Typography>
      )}
      {form.fecha_limite && (
        <Typography sx={{ fontSize: "0.78rem", color: "#c62828", mt: 0.5 }}>
          ⏰ Vence: {new Date(form.fecha_limite).toLocaleDateString("es-MX")}
        </Typography>
      )}
      {form.boton_label && (
        <Button size="small" variant="contained"
          sx={{ mt: 1, backgroundColor: t.color, "&:hover": { backgroundColor: t.color }, borderRadius: 999,
            textTransform: "none", fontSize: "0.78rem" }}>
          {form.boton_label}
        </Button>
      )}
      {form.es_principal && (
        <Chip label="⭐ PRINCIPAL" size="small"
          sx={{ mt: 0.8, backgroundColor: "#c8102e", color: "#fff", fontWeight: 700, fontSize: "0.68rem" }} />
      )}
    </Box>
  );
}

// ── Drawer: Crear / Editar aviso ─────────────────────────────
function AvisoDrawer({ open, onClose, avisoEdit, onSaved }) {
  const isNew = !avisoEdit;
  const emptyForm = {
    titulo: "", tipo: "informativo", mensaje: "", programa: "TODOS",
    boton_label: "", boton_url: "",
    fecha_evento: "", ubicacion: "", fecha_limite: "",
    fecha_inicio: "", fecha_fin: "",
    estado: "activo", programar: false,
    es_principal: 0, // Nuevo campo
  };

  const [form, setForm]     = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [programasList, setProgramasList] = useState(["TODOS"]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      // Cargar programas dinámicamente
      const cargarProgramas = async () => {
        try {
         const data = await apiFetch("/admin/catalog/programs");
const codigos = (data.programs || []).map(p => p.code).filter(Boolean);
          setProgramasList(["TODOS", ...codigos]);
        } catch (e) {
          console.warn("No se pudieron cargar programas:", e);
          setProgramasList(["TODOS", "SV", "TUM", "COM", "VOL"]); // fallback
        }
      };
      cargarProgramas();

      // Cargar formulario según si es edición o nuevo
      if (avisoEdit) {
        setForm({
          titulo:      avisoEdit.titulo      || "",
          tipo:        avisoEdit.tipo        || "informativo",
          mensaje:     avisoEdit.mensaje     || "",
          programa:    avisoEdit.programa    || "TODOS",
          boton_label: avisoEdit.boton_label || "",
          boton_url:   avisoEdit.boton_url   || "",
          fecha_evento:avisoEdit.fecha_evento ? avisoEdit.fecha_evento.slice(0,16) : "",
          ubicacion:   avisoEdit.ubicacion   || "",
          fecha_limite:avisoEdit.fecha_limite ? avisoEdit.fecha_limite.slice(0,16) : "",
          fecha_inicio:avisoEdit.fecha_inicio ? avisoEdit.fecha_inicio.slice(0,16) : "",
          fecha_fin:   avisoEdit.fecha_fin   ? avisoEdit.fecha_fin.slice(0,16) : "",
          estado:      avisoEdit.estado      || "activo",
          programar:   !!(avisoEdit.fecha_inicio || avisoEdit.fecha_fin),
          es_principal: avisoEdit.es_principal || 0,
        });
      } else {
        setForm(emptyForm);
      }
      setError(null);
    }
  }, [open, avisoEdit]);

  const handleSave = async () => {
    if (!form.titulo.trim()) { setError("El título es requerido."); return; }
    if (!form.mensaje.trim()) { setError("El mensaje es requerido."); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        titulo:      form.titulo,
        tipo:        form.tipo,
        mensaje:     form.mensaje,
        programa:    form.programa,
        boton_label: form.boton_label || null,
        boton_url:   form.boton_url   || null,
        fecha_evento:form.fecha_evento || null,
        ubicacion:   form.ubicacion   || null,
        fecha_limite:form.fecha_limite || null,
        fecha_inicio:form.programar ? (form.fecha_inicio || null) : null,
        fecha_fin:   form.programar ? (form.fecha_fin    || null) : null,
        estado:      form.estado,
        es_principal: form.es_principal || 0, // Nuevo campo
      };
      if (isNew) {
        await apiFetch("/avisos/admin", { method: "POST", body: JSON.stringify(body) });
      } else {
        await apiFetch(`/avisos/admin/${avisoEdit.id}`, { method: "PATCH", body: JSON.stringify(body) });
      }
      onSaved(isNew ? "Aviso publicado." : "Aviso actualizado.");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const t = TIPO_CONFIG[form.tipo] || TIPO_CONFIG.informativo;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
        sx={{ zIndex: 1300 }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, p: 0, display: "flex", flexDirection: "column" } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafafa" }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
          {isNew ? "Nuevo aviso" : "Editar aviso"}
        </Typography>
        <IconButton onClick={onClose} size="small"><FaTimes size={14} /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* 1. Datos básicos */}
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#555", mb: 1.5 }}>1. Datos básicos</Typography>

        <TextField label="Título del aviso *" fullWidth size="small" value={form.titulo}
          onChange={e => set("titulo", e.target.value)} sx={{ mb: 2 }} />

        {/* Programa chips */}
        <Typography sx={{ fontSize: "0.8rem", color: "#777", mb: 0.8 }}>Programa destino</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {programasList.map(p => (
            <Chip key={p} label={p} size="small" onClick={() => set("programa", p)}
              sx={{ fontWeight: 700, cursor: "pointer",
                backgroundColor: form.programa === p ? "#e60000" : "#f5f5f5",
                color: form.programa === p ? "#fff" : "#333",
                "&:hover": { backgroundColor: form.programa === p ? "#b30000" : "#eee" } }} />
          ))}
        </Box>

        {/* Toggle Principal - NUEVO */}
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.es_principal}
                onChange={e => set("es_principal", e.target.checked ? 1 : 0)}
                size="small"
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c8102e" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c8102e" } }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#2d233a" }}>
                📌 Mostrar como aviso principal en INICIO
                <Typography component="span" sx={{ fontSize: "0.75rem", color: "#888", fontWeight: 400, ml: 0.5 }}>
                  (solo 1 visible a la vez)
                </Typography>
              </Typography>
            }
          />
        </Box>

        {/* Tipo cards */}
        <Typography sx={{ fontSize: "0.8rem", color: "#777", mb: 0.8 }}>Tipo de aviso *</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {Object.entries(TIPO_CONFIG).map(([key, cfg]) => (
            <Box key={key} onClick={() => set("tipo", key)}
              sx={{ flex: "1 1 100px", border: `2px solid ${form.tipo === key ? cfg.color : "#eee"}`,
                borderRadius: 2, p: 1.2, cursor: "pointer", textAlign: "center",
                backgroundColor: form.tipo === key ? cfg.bg : "#fff",
                transition: "0.15s", "&:hover": { borderColor: cfg.color } }}>
              <Typography sx={{ fontSize: "1.2rem" }}>{cfg.emoji}</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: cfg.color }}>{cfg.label}</Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 2. Contenido */}
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#555", mb: 1.5 }}>2. Contenido del aviso</Typography>

        <TextField label="Mensaje *" fullWidth size="small" multiline rows={3}
          value={form.mensaje} onChange={e => set("mensaje", e.target.value)}
          inputProps={{ maxLength: 300 }}
          helperText={`${form.mensaje.length}/300`} sx={{ mb: 2 }} />

        {form.tipo === "evento" && (
          <>
            <TextField label="Fecha y hora del evento" fullWidth size="small" type="datetime-local"
              value={form.fecha_evento} onChange={e => set("fecha_evento", e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
            <TextField label="Ubicación" fullWidth size="small"
              value={form.ubicacion} onChange={e => set("ubicacion", e.target.value)} sx={{ mb: 1.5 }} />
          </>
        )}

        <TextField label="Texto del botón (opcional)" fullWidth size="small"
          value={form.boton_label} onChange={e => set("boton_label", e.target.value)}
          placeholder="Ver más" sx={{ mb: 1.5 }} />

        {form.boton_label && (
          <TextField label="URL del botón" fullWidth size="small"
            value={form.boton_url} onChange={e => set("boton_url", e.target.value)}
            placeholder="https://..." sx={{ mb: 1.5 }} />
        )}

        <Divider sx={{ my: 2 }} />

        {/* 3. Programación */}
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#555", mb: 1 }}>3. Programación (opcional)</Typography>
        <FormControlLabel
          control={<Switch checked={form.programar} onChange={e => set("programar", e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: "0.83rem" }}>Programar duración del aviso</Typography>}
          sx={{ mb: 1.5 }} />

        {form.programar && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField label="Fecha inicio" size="small" type="datetime-local"
              value={form.fecha_inicio} onChange={e => set("fecha_inicio", e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 180 }} />
            <TextField label="Fecha fin" size="small" type="datetime-local"
              value={form.fecha_fin} onChange={e => set("fecha_fin", e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 180 }} />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* 4. Vista previa */}
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#555", mb: 0.5 }}>4. Vista previa en vivo</Typography>
        <AvisoPreview form={form} />
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: "1px solid #eee", display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#777" }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ backgroundColor: "#e60000", "&:hover": { backgroundColor: "#b30000" }, textTransform: "none", fontWeight: 700 }}>
          {saving && <CircularProgress size={15} sx={{ color: "#fff", mr: 1 }} />}
          {isNew ? "Publicar aviso" : "Guardar cambios"}
        </Button>
      </Box>
    </Drawer>
  );
}

// ── Componente principal ─────────────────────────────────────
export default function Avisos() {
  const [avisos,   setAvisos]   = useState([]);
  const [stats,    setStats]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [tab,      setTab]      = useState("todos");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [avisoEdit,  setAvisoEdit]  = useState(null);
  const [snack,    setSnack]    = useState({ open: false, msg: "", severity: "success" });
  const iniciado = useRef(false);

  const cargar = async (estadoFilter) => {
    setLoading(true); setError(null);
    try {
      const q = estadoFilter && estadoFilter !== "todos" ? `?estado=${estadoFilter}` : "";
      const [data, statsData] = await Promise.all([
        apiFetch(`/avisos/admin${q}`),
        apiFetch("/avisos/admin/stats"),
      ]);
      setAvisos(data.avisos || []);
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;
    const a = auth.currentUser;
    const init = () => cargar("todos");
    if (a) { init(); return; }
    const unsub = auth.onAuthStateChanged(u => { if (u) { unsub(); init(); } });
    return () => unsub();
  }, []); // eslint-disable-line

  useEffect(() => { cargar(tab); }, [tab]); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este aviso?")) return;
    try {
      await apiFetch(`/avisos/admin/${id}`, { method: "DELETE" });
      setSnack({ open: true, msg: "Aviso eliminado.", severity: "success" });
      cargar(tab);
    } catch (err) {
      setSnack({ open: true, msg: err.message, severity: "error" });
    }
  };

  const TABS = [
    { key: "todos",      label: "Todos" },
    { key: "activo",     label: `Activos (${stats.activos || 0})` },
    { key: "programado", label: `Programados (${stats.programados || 0})` },
    { key: "expirado",   label: "Expirados" },
    { key: "borrador",   label: `Borradores (${stats.borradores || 0})` },
  ];

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", pt: 0, mt: -2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#111", fontFamily: "'Arial Black', sans-serif", letterSpacing: "-0.5px" }}>
            GESTIÓN DE AVISOS
          </Typography>
          <Typography sx={{ color: "#777", fontSize: "0.875rem", mt: 0.3 }}>
            Comunica de forma efectiva a todos los usuarios del sistema.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FaPlus size={13} />}
          onClick={() => { setAvisoEdit(null); setDrawerOpen(true); }}
          sx={{ backgroundColor: "#e60000", color: "#fff", fontWeight: 700, fontSize: "0.85rem",
            textTransform: "none", px: 2.5, py: 1, borderRadius: 2,
            boxShadow: "0 4px 12px rgba(230,0,0,0.3)", "&:hover": { backgroundColor: "#b30000" } }}>
          Crear aviso
        </Button>
      </Box>

      {/* Stats cards */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {[
          { label: "Urgentes",   value: stats.urgentes   || 0, color: "#e60000", sub: "Requieren atención" },
          { label: "Activos",    value: stats.activos    || 0, color: "#2e7d32", sub: "Publicados actualmente" },
          { label: "Programados",value: stats.programados|| 0, color: "#f57f17", sub: "Próximos a publicarse" },
          { label: "Borradores", value: stats.borradores || 0, color: "#757575", sub: "Aún no publicados" },
        ].map(s => (
          <Box key={s.label} sx={{ flex: "1 1 160px", border: `1px solid #eee`, borderRadius: 3, p: 2, backgroundColor: "#fafafa" }}>
            <Typography sx={{ fontSize: "0.78rem", color: "#777", fontWeight: 600 }}>{s.label}</Typography>
            <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: s.color, lineHeight: 1.1 }}>{s.value}</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#aaa", mt: 0.3 }}>{s.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* Tabs */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
        {TABS.map(t => (
          <Button key={t.key} size="small" variant={tab === t.key ? "contained" : "outlined"}
            onClick={() => setTab(t.key)}
            sx={{ textTransform: "none", fontWeight: tab === t.key ? 700 : 400, borderRadius: 999,
              backgroundColor: tab === t.key ? "#e60000" : "transparent",
              borderColor: tab === t.key ? "#e60000" : "#ddd",
              color: tab === t.key ? "#fff" : "#555",
              "&:hover": { backgroundColor: tab === t.key ? "#b30000" : "#f5f5f5" } }}>
            {t.label}
          </Button>
        ))}
      </Box>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#e60000" }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : avisos.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "#aaa" }}>
          <FaBell size={32} />
          <Typography sx={{ mt: 1 }}>No hay avisos en esta categoría.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {avisos.map(av => {
            const t = TIPO_CONFIG[av.tipo]   || TIPO_CONFIG.informativo;
            const e = ESTADO_CONFIG[av.estado] || ESTADO_CONFIG.borrador;
            return (
              <Box key={av.id} sx={{ border: "1px solid #eee", borderRadius: 3, p: 2,
                borderLeft: `4px solid ${t.color}`, backgroundColor: "#fff",
                display: "flex", alignItems: "flex-start", gap: 2,
                "&:hover": { backgroundColor: "#fafafa" } }}>
                {/* Icono */}
                <Box sx={{ width: 40, height: 40, borderRadius: 2, backgroundColor: t.bg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: t.color, flexShrink: 0 }}>
                  {t.icon}
                </Box>
                {/* Contenido */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{av.titulo}</Typography>
                    <Chip label={t.label} size="small"
                      sx={{ backgroundColor: t.bg, color: t.color, fontWeight: 700, fontSize: "0.68rem", height: 20 }} />
                    <Chip label={e.label} size="small"
                      sx={{ backgroundColor: e.bg, color: e.color, fontWeight: 700, fontSize: "0.68rem", height: 20 }} />
                    {av.es_principal && (
                      <Chip label="⭐ PRINCIPAL" size="small"
                        sx={{ backgroundColor: "#c8102e", color: "#fff", fontWeight: 700, fontSize: "0.68rem", height: 20 }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: "0.82rem", color: "#555" }}>{av.mensaje}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#aaa", mt: 0.5 }}>
                    {av.programa} · {av.fecha_inicio ? `${new Date(av.fecha_inicio).toLocaleDateString("es-MX")}` : "Inmediato"}
                    {av.fecha_fin ? ` - ${new Date(av.fecha_fin).toLocaleDateString("es-MX")}` : ""}
                  </Typography>
                </Box>
                {/* Acciones */}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => { setAvisoEdit(av); setDrawerOpen(true); }}
                      sx={{ color: "#e60000" }}>
                      <FaPencilAlt size={13} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => handleDelete(av.id)}
                      sx={{ color: "#ccc", "&:hover": { color: "#e60000" } }}>
                      <FaTrash size={13} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Drawer */}
      <AvisoDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        avisoEdit={avisoEdit}
        onSaved={(msg) => { setSnack({ open: true, msg, severity: "success" }); cargar(tab); }} />

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
